import React, { useMemo, useEffect } from "react";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "@/components/ui/Markdown";
import ReactMarkdown from "react-markdown";
import useScrollSpy from "../hooks/useScrollSpy";
import VerticalSectionNav from "../component/VerticalSectionNav";

const extractHeading = (section) => {
  const match = section.match(/^###?\s+(.+)/m);
  const raw = match
    ? match[1].replace(/\*\*/g, "").replace(/^\d+\.\s+/, "").trim()
    : section.slice(0, 30).trim() || "Section";

  return raw
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const Overview = ({ data }) => {
  const pages = useMemo(() => {
    const content = data?.detailed_analysis || "";
    if (!content.trim()) return [{ id: "empty", label: "Analysis", content: "No analysis available." }];

    return content
      .split(/(?=^###?\s)/gm)
      .filter(Boolean)
      .map((section, i) => ({
        id: `section-${i}`,
        label: extractHeading(section),
        content: section,
      }));
  }, [data?.detailed_analysis]);

  const pageIds = useMemo(() => pages.map((p) => p.id), [pages]);

  const { activeId, setActiveId, containerRef, registerSectionRef, scrollToSection } = useScrollSpy(pageIds);

  useEffect(() => {
    setActiveId(pages[0]?.id ?? null);
  }, [data?.detailed_analysis, pages, setActiveId]);

  return (
    /*
     * `h-full` + `overflow-hidden` on the outer shell so this component
     * fills whatever height the parent <main> gives it without overflowing.
     */
    <div className="flex h-full overflow-hidden">

      {/* Vertical nav — pinned at the left, never scrolls away */}
      {pages.length > 1 && (
        <div className="shrink-0 py-4 pl-4 min-h-0 border-r border-border-primary overflow-y-auto custom-scrollbar">
          <VerticalSectionNav sections={pages} activeSection={activeId} onSelect={scrollToSection} />
        </div>
      )}

      {/* Scrollable content area */}
      <div ref={containerRef} className="flex-1 min-w-0 overflow-y-auto px-6 pb-6 custom-scrollbar">
        {pages.map((page) => (
          <div key={page.id} ref={registerSectionRef(page.id)} data-section-id={page.id} className="scroll-mt-2">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {page.content}
            </ReactMarkdown>
          </div>
        ))}
      </div>

    </div>
  );
};

export default Overview;